import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { useI18n } from "@/i18n";

const STORAGE_KEY = "paynoravello_consent_v2";
const VISIT_KEY = "paynoravello_consent_seen";

function gtagConsentUpdate(consent) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  gtag("consent", "update", {
    ad_storage: consent.ad_storage,
    ad_user_data: consent.ad_user_data,
    ad_personalization: consent.ad_personalization,
    analytics_storage: consent.analytics_storage,
    personalization_storage: consent.personalization_storage,
  });
}

export default function CookieBanner() {
  const { t, lang } = useI18n();
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({
    analytics: false,
    ads: false,
    personalization: false,
  });

  useEffect(() => {
    try {
      const seen = localStorage.getItem(VISIT_KEY);
      if (!seen) setVisible(true);
    } catch (_) {
      setVisible(true);
    }
  }, []);

  const persist = (consent) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
      localStorage.setItem(VISIT_KEY, "1");
    } catch (_) {}
    gtagConsentUpdate(consent);
    setVisible(false);
  };

  const acceptAll = () => {
    persist({
      ad_storage: "granted",
      ad_user_data: "granted",
      ad_personalization: "granted",
      analytics_storage: "granted",
      personalization_storage: "granted",
    });
  };

  const rejectAll = () => {
    persist({
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: "denied",
      personalization_storage: "denied",
    });
  };

  const savePreferences = () => {
    persist({
      ad_storage: prefs.ads ? "granted" : "denied",
      ad_user_data: prefs.ads ? "granted" : "denied",
      ad_personalization: prefs.ads ? "granted" : "denied",
      analytics_storage: prefs.analytics ? "granted" : "denied",
      personalization_storage: prefs.personalization ? "granted" : "denied",
    });
  };

  if (!visible) return null;

  const base = `/${lang}`;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 px-3 pb-3 sm:px-6 sm:pb-6"
      role="dialog"
      aria-live="polite"
      aria-label={t("cookieBanner.title")}
      data-testid="cookie-banner"
    >
      <div className="mx-auto max-w-5xl rounded-2xl border border-slate-200 bg-white shadow-xl">
        {!showPrefs ? (
          <div className="p-5 sm:p-7 grid md:grid-cols-[auto_1fr_auto] gap-4 md:gap-6 md:items-center">
            <div className="hidden md:grid place-items-center h-14 w-14 rounded-xl bg-[#E8F5E9] text-[#006837]">
              <Cookie className="h-7 w-7" aria-hidden="true" />
            </div>
            <div>
              <h2 className="font-serif-heading text-lg font-bold text-slate-900">
                {t("cookieBanner.title")}
              </h2>
              <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                {t("cookieBanner.description")}{" "}
                <Link
                  to={`${base}/cookie-policy`}
                  className="text-[#006837] font-semibold underline"
                  data-testid="cookie-banner-policy-link"
                >
                  {t("footer.linkCookies")}
                </Link>
              </p>
            </div>
            <div className="flex flex-col sm:flex-row md:flex-col gap-2 md:min-w-[220px]">
              <button
                type="button"
                onClick={acceptAll}
                data-testid="cookie-accept-button"
                className="inline-flex justify-center items-center rounded-md bg-[#006837] hover:bg-[#00512b] px-4 py-2.5 text-sm font-semibold text-white shadow"
              >
                {t("cookieBanner.acceptAll")}
              </button>
              <button
                type="button"
                onClick={rejectAll}
                data-testid="cookie-reject-button"
                className="inline-flex justify-center items-center rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
              >
                {t("cookieBanner.rejectAll")}
              </button>
              <button
                type="button"
                onClick={() => setShowPrefs(true)}
                data-testid="cookie-preferences-button"
                className="inline-flex justify-center items-center rounded-md px-4 py-2.5 text-sm font-semibold text-[#134074] hover:underline"
              >
                {t("cookieBanner.preferences")}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 sm:p-7" data-testid="cookie-preferences-panel">
            <h2 className="font-serif-heading text-lg font-bold text-slate-900">
              {t("cookieBanner.prefTitle")}
            </h2>
            <p className="mt-1 text-sm text-slate-600">{t("cookieBanner.prefIntro")}</p>
            <div className="mt-4 grid gap-3">
              <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50">
                <input type="checkbox" checked disabled className="mt-1 h-4 w-4 accent-[#006837]" />
                <div>
                  <p className="font-semibold text-slate-900 text-sm">{t("cookieBanner.prefNecessary")}</p>
                  <p className="text-xs text-slate-600">{t("cookieBanner.prefNecessaryDesc")}</p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200">
                <input
                  type="checkbox"
                  checked={prefs.analytics}
                  onChange={(e) => setPrefs((p) => ({ ...p, analytics: e.target.checked }))}
                  data-testid="cookie-pref-analytics"
                  className="mt-1 h-4 w-4 accent-[#006837]"
                />
                <div>
                  <p className="font-semibold text-slate-900 text-sm">{t("cookieBanner.prefAnalytics")}</p>
                  <p className="text-xs text-slate-600">{t("cookieBanner.prefAnalyticsDesc")}</p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200">
                <input
                  type="checkbox"
                  checked={prefs.ads}
                  onChange={(e) => setPrefs((p) => ({ ...p, ads: e.target.checked }))}
                  data-testid="cookie-pref-ads"
                  className="mt-1 h-4 w-4 accent-[#006837]"
                />
                <div>
                  <p className="font-semibold text-slate-900 text-sm">{t("cookieBanner.prefAds")}</p>
                  <p className="text-xs text-slate-600">{t("cookieBanner.prefAdsDesc")}</p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200">
                <input
                  type="checkbox"
                  checked={prefs.personalization}
                  onChange={(e) => setPrefs((p) => ({ ...p, personalization: e.target.checked }))}
                  data-testid="cookie-pref-personalization"
                  className="mt-1 h-4 w-4 accent-[#006837]"
                />
                <div>
                  <p className="font-semibold text-slate-900 text-sm">{t("cookieBanner.prefPersonalization")}</p>
                  <p className="text-xs text-slate-600">{t("cookieBanner.prefPersonalizationDesc")}</p>
                </div>
              </label>
            </div>
            <div className="mt-5 flex flex-col sm:flex-row gap-2 sm:justify-end">
              <button
                type="button"
                onClick={rejectAll}
                data-testid="cookie-preferences-reject"
                className="inline-flex justify-center items-center rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800"
              >
                {t("cookieBanner.rejectAll")}
              </button>
              <button
                type="button"
                onClick={savePreferences}
                data-testid="cookie-preferences-save"
                className="inline-flex justify-center items-center rounded-md bg-[#006837] hover:bg-[#00512b] px-4 py-2.5 text-sm font-semibold text-white"
              >
                {t("cookieBanner.savePreferences")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
